import type * as React from "react";
import {
  AlertTriangle,
  FilePlus2,
  Replace,
  Plus,
  Trash2,
  Edit3,
} from "lucide-react";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { AndoLogo } from "../AndoLogo";
import type { ActionFieldValue } from "../ActionFieldEditor";
import { VendorPreviewShell, VendorPreviewField } from "./VendorPreviewShell";

/**
 * DocumentForkPreview — visual preview for `document_edit` and
 * `document_fork_edit` action proposals.
 *
 * UX contract (v2 SOTA — Action Drawer + Source Drawer co-presence,
 * §A.7 + §A.11 of the rewrite plan):
 *
 * - The **Action Drawer** mounts THIS preview at the top of the
 *   ``documents`` workspace tab. It shows what Ando wants to do:
 *   ``source.docx → new_title.docx`` plus the list of patch operations.
 * - The **Source Drawer** (mounted underneath, opened automatically by
 *   ``useActionRouter`` when the proposal arrives) shows the source
 *   document itself in artifact mode — the user sees both the base
 *   and the proposed mutation simultaneously, then confirms with one
 *   click on the ActionDrawer footer.
 *
 * Why a separate preview component and not the legacy `ActionProposalCard`:
 *
 * - Decoupled from any DOM structure: pure schema-driven render, fits
 *   inside the standard ``ActionDrawer.preview`` slot (parity with
 *   ``EmailSendDraftPreview``, ``CalendarCreateEventPreview``,
 *   ``FicCreateInvoicePreview``).
 * - Read-only by design: the editable surface lives in the ActionDrawer's
 *   ``ActionFieldEditor`` (``new_title`` is the only practical edit
 *   target; the ``edits`` array is the BE's structured patch payload and
 *   editing it manually would be footgun-prone).
 * - Renders BOTH branches (fork + in-place edit) by inspecting
 *   ``isFork``. Inline edits don't have a ``new_title`` step so we
 *   skip the source→destination arrow and just show the patch list.
 *
 * Wire shape contract (BE-side: see ``DocumentEditPreview`` and
 * ``propose_document_fork_edit`` tool docstring):
 *
 *   proposedFields = {
 *     source_document_id?: string,   // weaviate_doc_id of base file
 *     source_document_title?: string, // display label
 *     new_title?: string,             // fork-edit only; absent on in-place
 *     edits: Array<{ type: string, ... }>, // patch ops (text_replace, …)
 *     description?: string,           // one-liner from the agent
 *   }
 *
 * Missing fields render as a graceful "—" so a partial WS payload doesn't
 * blow up the preview (parity with the other ``*Preview`` components).
 */

export interface DocumentForkPreviewProps {
  /** Live field values (proposed merged with edits). */
  proposedFields: Readonly<Record<string, ActionFieldValue>>;
  /** When provided (post-execution), draws the executed copy. */
  executedFields?: Readonly<Record<string, ActionFieldValue>>;
  className?: string;
}

/** Narrow `ActionFieldValue` → string with `""` fallback. */
const asString = (v: ActionFieldValue | undefined): string =>
  typeof v === "string" ? v : v === null || v === undefined ? "" : String(v);

interface EditOp {
  type: string;
  // The rest is op-specific — we intentionally keep this loose because
  // the BE evolves the patch shape per source type (xlsx vs docx vs pptx).
  [k: string]: unknown;
}

/**
 * Map ``edits[].type`` (BE-side patch operation) → (label, icon).
 *
 * Keep this table the SINGLE source of truth. If you add a new patch
 * type backend-side (e.g. ``"add_chart"``), add the entry here and the
 * preview picks it up across both fork + in-place flows.
 */
const EDIT_OP_META: Readonly<
  Record<string, { label: MessageDescriptor; Icon: React.ComponentType<{ className?: string }> }>
> = {
  // Document (.docx)
  text_replace: { label: msg`Sostituzione testo`, Icon: Replace },
  add_paragraph: { label: msg`Aggiungi paragrafo`, Icon: Plus },
  delete_paragraph: { label: msg`Elimina paragrafo`, Icon: Trash2 },
  add_table: { label: msg`Aggiungi tabella`, Icon: Plus },
  add_doc_image: { label: msg`Aggiungi immagine`, Icon: Plus },
  add_list_item: { label: msg`Aggiungi voce lista`, Icon: Plus },
  // Spreadsheet (.xlsx)
  add_row: { label: msg`Aggiungi riga`, Icon: Plus },
  cell_update: { label: msg`Aggiorna cella`, Icon: Edit3 },
  delete_row: { label: msg`Elimina riga`, Icon: Trash2 },
  set_formula: { label: msg`Imposta formula`, Icon: Edit3 },
  add_sheet_image: { label: msg`Aggiungi immagine al foglio`, Icon: Plus },
  // Presentation (.pptx)
  add_slide: { label: msg`Aggiungi slide`, Icon: Plus },
  delete_slide: { label: msg`Elimina slide`, Icon: Trash2 },
  update_slide_title: { label: msg`Aggiorna titolo slide`, Icon: Edit3 },
  update_slide_content: { label: msg`Aggiorna contenuto slide`, Icon: Edit3 },
  add_slide_image: { label: msg`Aggiungi immagine slide`, Icon: Plus },
  add_slide_shape: { label: msg`Aggiungi forma slide`, Icon: Plus },
  add_slide_chart: { label: msg`Aggiungi grafico slide`, Icon: Plus },
};

const FALLBACK_OP = { label: msg`Modifica`, Icon: Edit3 } as const;

function pickEditMeta(type: string): { label: MessageDescriptor; Icon: React.ComponentType<{ className?: string }> } {
  return EDIT_OP_META[type] ?? FALLBACK_OP;
}

/** Best-effort short summary string for an edit op (used as caption). */
function summarizeEdit(op: EditOp): string | null {
  // text_replace: "old text" → "new text"
  if (op.type === "text_replace") {
    const search = typeof op.search === "string" ? op.search : "";
    const replace = typeof op.replace === "string" ? op.replace : "";
    if (search || replace) {
      const s = search.length > 30 ? `${search.slice(0, 30)}…` : search;
      const r = replace.length > 30 ? `${replace.slice(0, 30)}…` : replace;
      return `«${s}» → «${r}»`;
    }
    return null;
  }
  // cell_update: B2 = "value"
  if (op.type === "cell_update" && op.cell && typeof op.cell === "object") {
    const cell = op.cell as Record<string, unknown>;
    const row = typeof cell.row === "number" ? cell.row : null;
    const col = typeof cell.col === "number" ? cell.col : null;
    const value = cell.value;
    if (row !== null && col !== null) {
      const label = typeof value === "string" ? `«${value}»` : String(value);
      return `(${row}, ${col}) = ${label}`;
    }
    return null;
  }
  // add_paragraph: "Lorem ipsum…"
  if (op.type === "add_paragraph" && typeof op.text === "string") {
    return op.text.length > 60 ? `${op.text.slice(0, 60)}…` : op.text;
  }
  // add_slide: "Slide Title"
  if (op.type === "add_slide" && typeof op.title === "string") {
    return op.title;
  }
  // update_slide_title: "New Title"
  if (op.type === "update_slide_title" && typeof op.title === "string") {
    return op.title;
  }
  return null;
}

/** Type guard: array of EditOp-ish objects. */
function asEditList(v: ActionFieldValue | undefined): readonly EditOp[] {
  if (!Array.isArray(v)) return [];
  // ``ActionFieldValue`` includes ``string[]`` (multiselect); edit-op arrays
  // are type-erased structured values, so widen to ``unknown[]`` before the
  // structural guard re-narrows each element to ``EditOp``.
  return (v as readonly unknown[]).filter(
    (e): e is EditOp =>
      !!e && typeof e === "object" && typeof (e as { type?: unknown }).type === "string",
  );
}

export const DocumentForkPreview: React.FC<DocumentForkPreviewProps> = ({
  proposedFields,
  executedFields,
  className,
}) => {
  const { i18n } = useLingui();
  const f = executedFields ?? proposedFields;

  const sourceTitle = asString(f.source_document_title);
  const newTitle = asString(f.new_title);
  const description = asString(f.description);
  const edits = asEditList(f.edits);

  // ``fork`` mode = a new file gets created. Detected by the presence of
  // ``new_title``. In-place edit (legacy ``propose_document_edit``) has
  // no ``new_title`` so the preview collapses to just the patch list.
  const isFork = newTitle.length > 0;

  // Patch operations rendered as hairline-divided sheet rows in the body slot
  // (one continuous sheet — no separate boxed card).
  const editsBody =
    edits.length > 0 ? (
      <ul className="flex flex-col">
        {edits.map((op, idx) => {
          const { label, Icon } = pickEditMeta(op.type);
          const summary = summarizeEdit(op);
          return (
            <li
              key={`${op.type}-${idx}`}
              className="flex items-start gap-2.5 px-4 py-2.5 text-[12.5px] text-ink-1 [&:not(:first-child)]:border-hairline [&:not(:first-child)]:border-t"
            >
              <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-3" />
              <div className="min-w-0">
                <span className="font-medium">{i18n._(label)}</span>
                {summary ? (
                  <span className="ml-1.5 text-ink-3">— {summary}</span>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    ) : (
      <p className="px-4 py-3 text-[12.5px] text-ink-3 italic">
        {i18n._(msg`Nessuna modifica specificata.`)}
      </p>
    );

  return (
    <VendorPreviewShell
      className={className}
      icon={<FilePlus2 className="h-4 w-4" />}
      title={
        isFork
          ? i18n._(msg`Nuova versione del documento`)
          : i18n._(msg`Modifica documento`)
      }
      caption={
        edits.length > 0
          ? `${edits.length} ${
              edits.length === 1
                ? i18n._(msg`modifica`)
                : i18n._(msg`modifiche`)
            }`
          : undefined
      }
      body={editsBody}
      footnote={
        !isFork ? (
          <span className="inline-flex items-start gap-1.5">
            <AlertTriangle className="mt-px h-3 w-3 shrink-0 text-ink-3" aria-hidden />
            <span>
              {i18n._(
                msg`Modifica in-place: il file originale verrà aggiornato. Per creare una copia, chiedi a Ando di farti una "versione" del documento.`,
              )}
            </span>
          </span>
        ) : undefined
      }
    >
      <VendorPreviewField
        label={isFork ? i18n._(msg`Base`) : i18n._(msg`File`)}
        value={sourceTitle || undefined}
      />
      {isFork ? (
        <VendorPreviewField
          label={i18n._(msg`Nuovo file`)}
          value={
            <span className="inline-flex items-center gap-1.5 text-ando-orange">
              <AndoLogo size={14} className="shrink-0" aria-hidden />
              <span className="truncate font-medium">{newTitle}</span>
            </span>
          }
        />
      ) : null}
      {description ? (
        <VendorPreviewField
          label={i18n._(msg`Descrizione`)}
          value={description}
          wide
        />
      ) : null}
    </VendorPreviewShell>
  );
};
DocumentForkPreview.displayName = "DocumentForkPreview";
